// Registro genérico de "fontes de notificação" pendentes, consumido pelo
// indicador fixo da sidebar (ver docs/adr/0001-sidebar-notification-indicator.md).
//
// Cada fonte é só {label, route, roles, count} -- adicionar uma nova fonte no
// futuro (ex: quando a central de notificações existir) é só chamar
// registerNotificationSource, sem tocar no componente do indicador.
//
// Singleton reativo (não um composable por-componente): a contagem de cada
// fonte é atualizada por quem já possui aquele dado (ex: o socket de DMs em
// AppLayout.vue), então o registro precisa sobreviver e ser o mesmo objeto
// em qualquer lugar que o importe.
import { reactive, computed } from 'vue'

const sources = reactive({})

/**
 * @param {string} key identificador único da fonte (ex: 'merito-pendente')
 * @param {{ label: string, route: string, query?: Record<string,string>, roles?: string[] }} meta
 *   query (opcional): query string do destino, ex: { tab: 'promocoes' } abre a aba certa.
 *   roles ausente/undefined = visível pra todo mundo (equivalente a ALL).
 */
export function registerNotificationSource(key, { label, route, query, roles }) {
  sources[key] = { label, route, query, roles, count: sources[key]?.count ?? 0 }
}

export function setNotificationCount(key, count) {
  if (sources[key]) sources[key].count = count
}

/**
 * Diz se o papel pode consultar a contagem da fonte. Os polls usam isto pra nem
 * chamar o endpoint que o backend ia recusar com 403 (cada recusa vira linha no
 * Logs de Erro). Fonte sem `roles` vale pra todos; fonte não registrada, pra ninguém.
 */
export function canSeeNotificationSource(key, userRole) {
  const s = sources[key]
  if (!s) return false
  return !s.roles || s.roles.includes(userRole)
}

export function unregisterNotificationSource(key) {
  delete sources[key]
}

/**
 * @param {string} userRole papel do usuário logado, pra filtrar fontes que
 *   ele não tem permissão de ver (mesmo critério das `roles` da sidebar).
 */
export function useNotificationSources(userRole) {
  const visible = computed(() =>
    Object.entries(sources)
      .filter(([, s]) => !s.roles || s.roles.includes(userRole))
      .map(([key, s]) => ({ key, ...s }))
  )
  const pending = computed(() => visible.value.filter(s => s.count > 0))
  const total   = computed(() => pending.value.reduce((sum, s) => sum + s.count, 0))
  return { pending, total }
}
